import { BuildingOffice2Icon, IdentificationIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { PlusIcon } from "@heroicons/react/24/solid";
import { clsx } from "clsx";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export interface Item {
  id: number;
  primaryText: string;
  secondaryText?: string;
  type: "user" | "building";
  subItems?: Item[];
}

export default function ItemsTable({
  items,
  onRemove,
  onAdd,
  showAdd,
  translationKeyPrefix = "settings",
}: {
  items: Item[];
  onRemove?: (id: number) => void;
  onAdd?: () => void;
  showAdd?: boolean;
  translationKeyPrefix?: string;
}) {
  const { t } = useTranslation();

  return (
    <table className="w-full border-separate border-spacing-y-0.5">
      <tbody>
        {items.map((item) => (
          <ItemRow key={item.id} item={item} onRemove={onRemove} />
        ))}
        <TableRow onClick={onAdd} className={clsx(!showAdd && "hidden")}>
          <PlusIcon className="w-7 h-7 p-1.5 rounded-full bg-gray-300" />
          <span>{t(`${translationKeyPrefix}.addNewAccess`)}</span>
        </TableRow>
      </tbody>
    </table>
  );
}

function ItemRow({ item, onRemove, depth = 0 }: { item: Item; onRemove?: (id: number) => void; depth?: number }) {
  return (
    <>
      <TableRow>
        {item.type === "user" ? (
          <IdentificationIcon className={`w-7 h-7 p-1 ml-${depth * 2}`} />
        ) : (
          <BuildingOffice2Icon className={`w-7 h-7 p-1 ml-${depth * 2}`} />
        )}
        <span>{item.primaryText}</span>
        <span>{item.secondaryText}</span>
        {onRemove ? (
          <XMarkIcon
            className="w-5 h-5 ml-auto mr-2 cursor-pointer invisible hover:visible group-hover:visible hover:text-dobu-orange-focus"
            onClick={() => onRemove(item.id)}
          />
        ) : (
          <span />
        )}
      </TableRow>
      {item.subItems &&
        item.subItems.map((subItem) => (
          <ItemRow key={item.id + "-" + subItem.id} item={subItem} onRemove={onRemove} depth={depth + 1} />
        ))}
    </>
  );
}

function TableRow({
  children,
  onClick,
  className,
}: {
  children: ReactNode[];
  onClick?: () => void;
  className?: string;
}) {
  return (
    <tr className={clsx("group", onClick && "cursor-pointer", "px-2 py-0.5", className)} onClick={onClick}>
      {children.map((child, index) => (
        <td
          key={index}
          className=" group-hover:bg-gray-200 first:rounded-l-full last:rounded-r-full"
          colSpan={index === children.length - 1 ? 6 - children.length : 1}
        >
          {child}
        </td>
      ))}
    </tr>
  );
}
